import * as React from 'react'

import {
  Button,
  InputCheckbox,
  InputChecker,
  InputSelect,
  InputText,
  Modal,
} from '@lmc/ui-kit'

import { useTranslation } from 'react-i18next'
import { Form } from 'antd'
import { useEffect, useState } from 'react'
import { showToast } from '../../../../common/utils'
import { Need, Skill, SkillCategory, SkillNeedTemplate } from '@lmc/ts-common'

type SkillNeedTemplateModalProps = {
  isModalVisible: boolean
  handleToogleDeleteModal: () => void
  handleToggleModal: () => void
  addSkillNeedTemplate?: (skill: SkillNeedTemplate) => Promise<unknown>
  updateSkillNeedTemplate?: (
    skill: SkillNeedTemplate,
    id: string
  ) => Promise<any>
  categoriesOptions?: SkillCategory[]
  needOptions?: Need[]
  skillOptions?: Skill[]
  skillNeedTemplate?: SkillNeedTemplate
  preferredPeopleNominative?: string
  fetchCategoriesOptions?: () => Promise<unknown>
  fetchNeedsOptions?: () => Promise<unknown>
  fetchSkillOptions?: () => Promise<unknown>
  createdNeedsIds: string[]
  createdSkillIds: string[]
}

export const SkillNeedTemplateModal = ({
  addSkillNeedTemplate,
  updateSkillNeedTemplate,
  handleToggleModal,
  handleToogleDeleteModal,
  isModalVisible,
  categoriesOptions,
  needOptions,
  skillOptions,
  skillNeedTemplate,
  preferredPeopleNominative,
  fetchCategoriesOptions,
  fetchNeedsOptions,
  fetchSkillOptions,
  createdNeedsIds,
  createdSkillIds,
}: SkillNeedTemplateModalProps): React.ReactElement => {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const onConfirm = () => {
    form.submit()
  }

  const [loading, setLoading] = useState(false)

  const [formChange, setFormChange] = useState<SkillNeedTemplate | undefined>(
    skillNeedTemplate
  )

  const handleCloseForm = () => {
    handleToggleModal()
    handleCleanForm()
  }

  const handleCleanForm = () => {
    form.resetFields()
    form.setFieldsValue({})
    setFormChange({} as SkillNeedTemplate)
    setLoading(false)
  }

  const skillNeedTemplateFormValues = {
    ...skillNeedTemplate,
    trainingRequired: !skillNeedTemplate?.trainingRequired,
    trainingValidFor:
      skillNeedTemplate?.trainingValidFor === 0
        ? undefined
        : skillNeedTemplate?.trainingValidFor,
    ...(!skillNeedTemplate?.trainingRequired && { trainingName: undefined }),
  }

  useEffect(() => {
    if (skillNeedTemplate) {
      form.setFieldsValue(skillNeedTemplateFormValues)
      setFormChange(skillNeedTemplateFormValues as SkillNeedTemplate)
      handleToggleModal()
    }
    if (!skillNeedTemplate && formChange) {
      handleCleanForm()
    }
  }, [skillNeedTemplate])

  const handleFinish = () => {
    fetchCategoriesOptions?.()
    fetchSkillOptions?.()
    fetchNeedsOptions?.()
    setLoading(false)
    showToast({ title: t('general.forms.successfullySaved') })
    handleCloseForm()
  }

  const onFinish = async ({
    categories,
    needs,
    skill,
    ...values
  }: {
    categories: string[]
    trainingRequired: boolean
    needs: string[]
    skill: string | string[]
  }) => {
    setLoading(true)
    const payload = {
      ...values,
      trainingRequired: !values.trainingRequired,
      ...((!!categories?.length || !!skillNeedTemplate?.categories?.length) && {
        categories,
      }),
      ...((!!needs?.length || !!skillNeedTemplate?.needs?.length) && {
        needs,
      }),
      skill: Array.isArray(skill) ? skill[0] : skill,
    } as unknown as SkillNeedTemplate

    let res: unknown

    if (skillNeedTemplate?.id) {
      try {
        res = await updateSkillNeedTemplate?.(payload, skillNeedTemplate.id)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }
    } else {
      try {
        res = await addSkillNeedTemplate?.(payload)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }
    }
    if (res) {
      handleFinish()
    } else {
      setLoading(false)
    }
  }

  const trainingValidForRequired =
    !formChange?.trainingRequired && !!formChange?.trainingName

  return (
    <Modal
      localisation={{
        title: t(
          `skillsAndNeeds.${skillNeedTemplate ? 'editSkill' : 'addNewSkill'}`
        ),
        confirmText: t(`general.buttons.confirm`),
        cancelText: t(`general.buttons.cancel`),
      }}
      onConfirm={onConfirm}
      onCancel={handleCloseForm}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: loading, variant: 'primary' }}
      extraFooterElement={
        skillNeedTemplate && (
          <Button variant="danger" onClick={handleToogleDeleteModal}>
            {t('skillsAndNeeds.deleteSkill')}
          </Button>
        )
      }
    >
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={(e) => setFormChange({ ...formChange, ...e })}
      >
        <InputSelect
          errorMessage={t('skillsAndNeeds.form.skillNameRequired')}
          type="skill"
          required
          label={t('skillsAndNeeds.form.skillName')}
          tooltip={t('skillsAndNeeds.form.skillNameTooltip')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.selectFromList')}
          options={
            skillOptions?.map((skill) => ({
              label: skill?.name || 'N/A',
              value: skill?.id,
              disabled: createdSkillIds
                ?.filter((item) => skillNeedTemplate?.skill !== item)
                .includes(skill.id),
            })) || []
          }
          showSearch
          onChange={(e) => {
            form.setFields([{ name: 'skill', value: e?.slice(-1) }])
          }}
          mode='tags'
          disabled={skillOptions?.some(item => item.id === formChange?.skill)}
        />
        <InputText
          type="trainingName"
          label={t('skillsAndNeeds.form.trainingName')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.trainingNameRequired')}
          style={{ width: '100%' }}
          disabled={formChange?.trainingRequired}
          required={!formChange?.trainingRequired}
          errorMessage={t('skillsAndNeeds.form.trainingNameIsRequired')}
          tooltip={t('skillsAndNeeds.form.trainingNameTooltip')}
        />
        <InputCheckbox
          withoutInput
          label={t('skillsAndNeeds.form.noTrainingRequired')}
          type="trainingRequired"
          onChange={(e) => {
            if (e.target.checked) {
              form.setFields([
                { name: 'trainingName', value: null, errors: [] },
              ])
              form.setFields([
                { name: 'trainingValidFor', value: null, errors: [] },
              ])
              setFormChange({
                ...formChange,
                trainingValidFor: null,
                trainingName: null,
              } as unknown as SkillNeedTemplate)
            }
          }}
        />
        <InputChecker
          errorMessage={t('skillsAndNeeds.form.validForRequired')}
          type="trainingValidFor"
          required={trainingValidForRequired}
          disabled={formChange?.trainingRequired}
          checkBoxLabel={t('skillsAndNeeds.form.doesNotExpire')}
          inputDescription={t('skillsAndNeeds.form.months')}
          label={t('skillsAndNeeds.form.trainingValidFor')}
          form={form}
          tooltip={t('skillsAndNeeds.form.trainingValidForTooltip')}
        />
        <InputSelect
          showSearch
          type="needs"
          label={t('skillsAndNeeds.form.coveredNeeds')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.selectFromList')}
          options={
            needOptions?.map((need) => ({
              label: need?.name || 'N/A',
              value: need?.id,
              disabled: createdNeedsIds
                ?.filter((item) => skillNeedTemplate?.needs?.[0] !== item)
                .includes(need.id),
            })) || []
          }
          mode="tags"
          tooltip={t('skillsAndNeeds.form.coveredNeedsTooltip', {
            variable: preferredPeopleNominative,
          })}
          errorMessage={t('skillsAndNeeds.form.needRequired')}
        />
        <InputSelect
          type="categories"
          mode="tags"
          showSearch
          label={t('skillsAndNeeds.form.category')}
          layout="column"
          placeholder={t('skillsAndNeeds.form.startTyping')}
          options={
            categoriesOptions?.map((category) => ({
              label: category.name || 'N/A',
              value: category.id,
            })) || []
          }
          tooltip={t('skillsAndNeeds.form.categoryTooltip')}
          onChange={(e) => {
            form.setFields([{ name: 'categories', value: e }])
          }}
        />
      </Form>
    </Modal>
  )
}

export default SkillNeedTemplateModal
